import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import CyberBackground from "@/components/CyberBackground";
import { EASE } from "@/components/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/content";

const HERO_LINES = [
  { text: "Digital products,", accent: false },
  { text: "engineered to move", accent: true },
  { text: "business forward.", accent: false },
];

const HERO_POINTS = ["A dedicated, professional UK team", "Fixed-scope, transparent pricing", "Security built in from day one"];

function StatusPill() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get("/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="inline-flex items-center gap-2.5 rounded-full border border-cyan-400/25 bg-cyan-400/5 px-4 py-2"
      data-testid="hero-status-pill"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
      </span>
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">
        TechAxis NOC · {status ? `${status.message} — ${status.uptime} uptime` : "All Systems Operational"}
      </span>
    </motion.div>
  );
}

export default function HeroSection() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28" onMouseMove={onMouseMove} data-testid="home-hero">
      <CyberBackground />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:px-8">
        <div>
          <StatusPill />
          <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-title">
            {HERO_LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-gradient" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
          >
            TechAxis Ltd. designs, builds and runs the websites, apps, cloud platforms and security
            that ambitious UK businesses rely on — from first sketch to 24/7 managed support.
          </motion.p>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-6 space-y-2.5"
          >
            {HERO_POINTS.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-sm text-slate-300">
                <Check className="h-4 w-4 text-cyan-400" />
                {p}
              </li>
            ))}
          </motion.ul>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/quote"
              data-testid="hero-get-quote-button"
              className="group flex items-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_35px_rgba(0,102,255,0.4)] transition-all hover:bg-cyan-400 hover:text-ink"
            >
              Get a Quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-explore-services-button"
              className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
            >
              Explore Services
            </Link>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: EASE }}
          style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 900 }}
          className="relative hidden lg:block"
          data-testid="hero-visual"
        >
          <div className="spotlight relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(3,7,18,0.8)]">
            <img src={IMAGES.code} alt="TechAxis engineering workstation with code" className="aspect-[4/3] w-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
          </div>
          <div className="glass animate-floaty absolute -left-8 -bottom-8 rounded-xl px-5 py-4" style={{ transform: "translateZ(50px)" }} data-testid="hero-stat-chip-projects">
            <p className="font-display text-2xl font-extrabold text-white">120+</p>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Projects shipped</p>
          </div>
          <div className="glass animate-floaty absolute -right-6 top-8 rounded-xl px-5 py-4" style={{ transform: "translateZ(40px)", animationDelay: "1.4s" }} data-testid="hero-stat-chip-uptime">
            <p className="font-display text-2xl font-extrabold text-cyan-300">99.99%</p>
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Managed uptime</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
